/**
 * This class utilizes wc-datepicker library.
 * https://sqrrl.github.io/wc-datepicker/#options
 */
howspace.components.datePicker = (() => {
    'use strict';

    /**
     * Check if a Date object is valid.
     * @param {Date} date - The date to check
     * @returns {boolean} - True if the date is valid, false otherwise
     */
    const isValidDate = (date) => {
        return date instanceof Date && !isNaN(date.getTime());
    };

    const getDateModifiedByInputs = (datePicker, date) => {
        let hours = datePicker.find('.hours-input').val();
        let minutes = datePicker.find('.minutes-input').val();
        const amPm = datePicker.find('.am-pm-select').val();

        if (hours) {
            hours = parseInt(hours);
            if (amPm === 'PM' && hours < 12) {
                hours += 12;
            } else if (amPm === 'AM' && hours === 12) {
                hours = 0;
            }
            date.setHours(hours);
        }

        if (minutes) {
            minutes = parseInt(minutes);
            date.setMinutes(minutes);
        }

        return date;
    };

    const selectDate = (e) => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        const time = datePicker.data('time') || '00:00:00';
        const date = getDateModifiedByInputs(datePicker, new Date(e.detail + 'T' + time));

        requestAnimationFrame(() => {
            // Check if date is valid before calling toISOString
            if (!isValidDate(date)) {
                console.error('Invalid date in selectDate:', e.detail);
                return;
            }
            datePicker.dataAndAttr('date', date.toISOString());
            updateDate(datePicker);

            if (datePicker.data('pick-time')) {
                // Put focus to the hours input.
                datePicker.find('.hours-input').trigger('select');
            } else {
                closeDatePicker(datePicker);
            }
        });
    };

    // Keys that open the calendar from the date input, like in a native date picker.
    const OPEN_KEYS = ['Enter', ' ', 'ArrowDown'];

    const getDateInput = (datePicker) => datePicker.find('.howspace-form-date-input');

    // Move focus to the focusable day of <wc-datepicker>, which is the selected date, today or the browsed date.
    const focusCalendar = (datePicker) => {
        const calendar = datePicker.find('wc-datepicker')[0];
        const target = calendar?.querySelector('.wc-datepicker__date[tabindex="0"]')
            ?? calendar?.querySelector('button:not([disabled]), select:not([disabled]), input:not([disabled])');
        target?.focus();
    };

    /**
     * Open the calendar.
     * @param {Event|{target: Element}} e - Event or object whose target is inside the date picker
     * @param {boolean} moveFocus - Move focus into the calendar once it is open
     */
    const openDatePicker = (e, moveFocus = false) => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));

        if (datePicker.is('.open')) {
            if (moveFocus) {
                focusCalendar(datePicker);
            }
            return;
        }

        if (datePicker.is('.opening') || datePicker.is('.closing') || datePicker.is('[disabled]')) {
            // Date picker is already opening or closing.
            return;
        }

        const wcDatePicker = datePicker.find('wc-datepicker');
        if (datePicker.data('date')) {
            // Update selected date to the <wc-datepicker> UI.
            wcDatePicker.val(new Date(datePicker.data('date')));
        }

        if (datePicker.data('append-time-on-open')) {
            const hoursInput = datePicker.find('.hours-input');
            const minutesInput = datePicker.find('.minutes-input');
            const amPmSelect = datePicker.find('.am-pm-select');

            // DatePicker also has a time selector, but it's not appended yet as a part of the picker UI.
            // Remove the data attribute, it's no longer needed once the date picker is opened for the first time.
            datePicker.removeDataAndAttr('append-time-on-open');

            // Append footer to the <wc-datepicker> UI with a small timeout to make sure that the internal
            // ".wc-datepicker" element is already rendered inside the <wc-datepicker> element.
            setTimeout(() => {
                wcDatePicker.find('.wc-datepicker').append(
                    datePicker.find('.howspace-date-picker-footer').removeClass('d-none')
                );
            }, 100);

            if (datePicker.hasClass('always-open')) {
                // Remove the OK button from the footer when date picker is always open.
                datePicker.find('.ok-btn').remove();
            }

            if (datePicker.data('date')) {
                // Update values of the hours, minutes and AM/PM inputs.
                const date = new Date(datePicker.data('date'));
                const options = {
                    hour: 'numeric',
                };

                if(!datePicker.data('has-24-hours-clock')) {
                    options.hour12 = true;
                }

                // This will render something like "3 am" or "19" depending on locale.
                // Split with space and use the first value as hours.
                const hourArr = date.toLocaleString(datePicker.data('locale'), options).split(' ');
                hoursInput.val(hourArr[0]);
                minutesInput.val(real.pad(date.getMinutes()));

                if (amPmSelect && hourArr[1]) {
                    amPmSelect.val(hourArr[1].toUpperCase());
                }
            }
        }

        datePicker.removeClass('closed').addClass('opening');
        if (!datePicker.hasClass('always-open')) {
            getDateInput(datePicker).attr('aria-expanded', 'true');
        }

        if (wcDatePicker.attr('static')) {
            wcDatePicker.css('position', 'static');
            wcDatePicker.css('display', 'block');
        } else {
            howspace.popover.open(datePicker, wcDatePicker);
        }

        $.Velocity(datePicker.find('wc-datepicker'), 'slideDown', {
            duration: howspace.getAnimationDuration(datePicker, '--calendar-animation-duration'),
            complete: () => {
                datePicker.removeClass('opening').addClass('open');
                howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_PICKER_OPENED);
                if (moveFocus) {
                    // Wait a frame so <wc-datepicker> has rendered the date set above.
                    requestAnimationFrame(() => focusCalendar(datePicker));
                }
            }
        });
    };

    const closeDatePicker = (datePicker) => {
        if (datePicker.hasClass('always-open')) {
            return;
        }

        const wcDatePicker = datePicker.find('wc-datepicker');
        const dateInput = getDateInput(datePicker);
        dateInput.attr('aria-expanded', 'false');

        const activeElement = document.activeElement;
        const returnFocus = activeElement && activeElement !== dateInput[0] && datePicker[0].contains(activeElement);
        if (returnFocus) {
            // Focus is in the calendar or on a button that is about to hide. Return it to the input, which must not
            // open the calendar again.
            focusInputWithoutOpening(dateInput[0]);
        }

        datePicker.removeClass('open').addClass('closing');
        $.Velocity(datePicker.find('wc-datepicker'), 'slideUp', {
            duration: howspace.getAnimationDuration(datePicker, '--calendar-animation-duration'),
            complete: () => {
                datePicker.removeClass('closing').addClass('closed');
                // The input can't take focus while a change handler has it disabled, so try again once the
                // calendar is hidden and focus would otherwise be lost.
                const lostFocus = document.activeElement === document.body
                    || datePicker.find('wc-datepicker')[0]?.contains(document.activeElement);
                if (returnFocus && lostFocus && !dateInput.prop('disabled')) {
                    focusInputWithoutOpening(dateInput[0]);
                }
                if (wcDatePicker.attr('static')) {
                    wcDatePicker.css('display', 'none');
                } else {
                    howspace.popover.close(datePicker, wcDatePicker);
                }
            }
        });
    };

    const handleChange = e => {
        const input = $(e.target);

        if (input.attr('type') === 'number') {
            let value = parseInt(input.val());
            const min = parseInt(input.attr('min'));
            const max = parseInt(input.attr('max'));

            if (value < min) {
                // Value is too low, reset value to minimum.
                value = min;
            }
            if (value > max) {
                // Value is too high, reset value to maximum.
                value = max;
            }

            if (input.is('.minutes-input') && value < 10) {
                // Pad minutes with zero if needed.
                value = real.pad(value);
            }

            if (parseInt(input.val()) !== value) {
                input.val(value);
            }
        }

        const datePicker = input.closest('.howspace-date-picker');
        if (datePicker) {
            const dateData = datePicker.data('date');
            // If there's no date data yet, we can't update the time
            if (!dateData) {
                return;
            }
            const date = getDateModifiedByInputs(datePicker, new Date(dateData));
            // Check if date is valid before calling toISOString
            if (!isValidDate(date)) {
                console.error('Invalid date in handleChange:', dateData);
                return;
            }
            datePicker.dataAndAttr('date', date.toISOString());
            updateDate(datePicker);
        }
    };

    const updateDate = (datePicker, triggerChange = true) => {
        if (!datePicker.data('date')) {
            // Date picker doesn't have date as data attribute.
            return;
        }

        const date = new Date(datePicker.data('date'));
        const dateInput = datePicker.find('.howspace-form-date-input');
        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        };

        if (datePicker.data('pick-time')) {
            options.hour = 'numeric';
            options.minute = 'numeric';
        }

        if(!datePicker.data('has-24-hours-clock')) {
            options.hour12 = true;
        }

        const time = datePicker.data('time');
        if (time) {
            const [hours, minutes, seconds] = time.split(':');
            if (!isNaN(hours)) {
                date.setHours(hours);
            }
            if (!isNaN(minutes)) {
                date.setMinutes(minutes);
            }
            if (!isNaN(seconds)) {
                date.setSeconds(seconds);
            }
        }

        let dateStr = new Intl.DateTimeFormat(datePicker.data('locale'), options).format(date);
        if (datePicker.data('locale') === 'fi-FI') {
            // Replace the "klo" or "klo " prefix with an empty string.
            dateStr = dateStr.replace('klo ', '').replace('klo', '');
        }

        dateInput.val(dateStr);
        dateInput.dataAndAttr('value', date.toISOString());
        datePicker.addClass('show-clear-date-btn');

        if (triggerChange) {
            howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_CHANGED, date.getTime());

            const compareTo = $(datePicker.data('compare-to'));
            const compareType = datePicker.data('compare-type');
            const compareRequired = datePicker.data('compare-required');

            if (compareTo.length > 0 && compareType) {

                if (!compareTo.data('date') && !compareRequired) {
                    return;
                }

                dateInput.siblings('.invalid-feedback').html('');
                howspace.form.clearInvalidField(dateInput);

                const compareDate = new Date(compareTo.data('date'));

                if (compareType === 'before' && date > compareDate) {
                    dateInput.siblings('.invalid-feedback').html(howspace.translate('common_date_must_before'));
                    howspace.form.markInvalidField(dateInput);
                    howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_COMPARED_FAILED);
                    return;
                }

                if (compareType === 'after' && date < compareDate) {
                    dateInput.siblings('.invalid-feedback').html(howspace.translate('common_date_must_after'));
                    howspace.form.markInvalidField(dateInput);
                    howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_COMPARED_FAILED);
                    return;
                }

                if (compareType === 'same' && date.getTime() !== compareDate.getTime()) {
                    dateInput.siblings('.invalid-feedback').html(howspace.translate('common_date_must_same'));
                    howspace.form.markInvalidField(dateInput);
                    howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_COMPARED_FAILED);
                    return;
                }

                // If we reach here, the date comparison passed.
                // Remove the tooltip and the failed class from the date picker.
                compareTo.find('.howspace-form-date-input').siblings('.invalid-feedback').html('');
                howspace.form.clearInvalidField(compareTo.find('.howspace-form-date-input'));
                howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_COMPARED_PASSED);
            }
        }
    };

    const handleBlur = e => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        requestAnimationFrame(() => {
            if (
                !document.activeElement ||
                document.activeElement.closest('.howspace-date-picker') !== datePicker[0]
            ) {
                // Focus moved away from this date picker component.
                closeDatePicker(datePicker);
            }
        });
    };

    const handleClickOk = e => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        updateDate(datePicker);
        closeDatePicker(datePicker);
    };

    const handleKeyDown = e => {
        if (e.key !== 'Escape') {
            // We are only interested when user hits Escape.
            return;
        }

        const datePicker = $(e.target.closest('.howspace-date-picker'));
        if (!datePicker.is('.open')) {
            // Date picker is not open, no further actions needed.
            return;
        }

        // Close the date picker and stop propagating the event further.
        e.stopImmediatePropagation();
        closeDatePicker(datePicker);
    };

    const clearDate = e => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        const dateInput = datePicker.find('.howspace-form-date-input');

        datePicker.removeClass('show-clear-date-btn');
        datePicker.dataAndAttr('date', '');
        dateInput.val('');
        dateInput.dataAndAttr('value', '');

        dateInput.siblings('.invalid-feedback').html('');
        howspace.form.clearInvalidField(dateInput);

        howspace.dispatchEvent(datePicker, howspace.components.datePicker.DATE_PICKER_CLEARED);

        closeDatePicker(datePicker);
    };

    // Remember how the date picker was pressed. A click on the input, or one that the input's own <label> forwards
    // to it, decides from this whether focus moves into the calendar.
    const handlePointerDown = e => {
        $(e.currentTarget).data('pointer-type', e.pointerType);
    };

    const forgetPointerType = e => {
        $(e.currentTarget).removeData('pointer-type');
    };

    const focusInputWithoutOpening = dateInput => {
        const datePicker = $(dateInput.closest('.howspace-date-picker'));
        datePicker.data('returning-focus', true);
        dateInput.focus();
        datePicker.removeData('returning-focus');
    };

    // The calendar opens when the input gets focus, as it always has. Focus stays in the input, and Enter, Space or
    // ArrowDown moves it into the calendar.
    const handleInputFocus = e => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        if (datePicker.data('returning-focus')) {
            return;
        }
        openDatePicker(e);
    };

    const handleInputClick = e => {
        const datePicker = $(e.target.closest('.howspace-date-picker'));
        const pointerType = datePicker.data('pointer-type');
        datePicker.removeData('pointer-type');

        // A mouse keeps focus in the input as before. Touch and screen reader activation, which may come without
        // any pointer event, move focus to the calendar so that it can be used right away.
        openDatePicker(e, pointerType !== 'mouse' && pointerType !== 'pen');
    };

    const handleInputKeyDown = e => {
        if (e.ctrlKey || e.metaKey || !OPEN_KEYS.includes(e.key)) {
            return;
        }

        // Also stops Enter from submitting the form and Space from scrolling the page.
        e.preventDefault();
        openDatePicker(e, true);
    };

    // The wrapper is not a <label>, so a click on the label text opens the calendar like a click on the input.
    const handleLabelTextClick = e => {
        const dateInput = getDateInput($(e.currentTarget).siblings('.howspace-date-picker'));
        if (dateInput.length === 0) {
            return;
        }
        dateInput[0].focus();
        openDatePicker({target: dateInput[0]});
    };

    // Prevent default when clicking non-inputs inside the date picker in order to fix Safari blur issues.
    const handleMouseDown = e => {
        if (!e.target.closest('input, select, textarea')) {
            e.preventDefault();
        }
    };

    const initWcDatepicker = (datePicker) => {
        const wcDatePicker = datePicker.find('wc-datepicker');
        const dateInput = datePicker.find('.howspace-form-date-input');
        const clearDateBtn = datePicker.find('.clear-date-btn');

        if (!datePicker.hasClass('always-open')) {
            // Blur event doesn't bubble so listen to the event in the capture phase.
            datePicker.on('mousedown', handleMouseDown);
            datePicker[0].addEventListener('blur', handleBlur, true);
            datePicker.on('pointerdown', handlePointerDown);
            dateInput.on('focus', handleInputFocus);
            dateInput.on('click', handleInputClick);
            datePicker.on('click', forgetPointerType);
            dateInput.on('keydown', handleInputKeyDown);
            datePicker.siblings('.howspace-form-label-text').on('click', handleLabelTextClick);
        }

        const calendarLabels = datePicker.data('calendar-labels');
        if (calendarLabels) {
            wcDatePicker[0].labels = calendarLabels;
        }

        datePicker.on('keydown', handleKeyDown);
        wcDatePicker.on('selectDate', selectDate);

        wcDatePicker[0].disableDate = (date) => {
            if (datePicker.data('disable-past')) {
                // Disable dates before today.
                const startOfToday = new Date();
                startOfToday.setHours(0, 0, 0, 0);
                return date.getTime() < startOfToday.getTime();
            } else if (datePicker.data('min-date') && datePicker.data('max-date')) {
                // Disable dates outside the range.
                return date.getTime() < new Date(datePicker.data('min-date')).getTime() || date.getTime() > new Date(datePicker.data('max-date')).getTime();
            } else if (datePicker.data('min-date')) {
                // Disable dates before the minimum date.
                return date.getTime() < new Date(datePicker.data('min-date')).getTime();
            } else if (datePicker.data('max-date')) {
                // Disable dates after the maximum date.
                return date.getTime() > new Date(datePicker.data('max-date')).getTime();
            }
        };

        updateDate(datePicker, false);

        clearDateBtn.on('click', clearDate);

        if (datePicker.data('pick-time')) {
            // Update date when hours, minutes and/or AM/PM selection changes.
            const hoursInput = datePicker.find('.hours-input');
            const minutesInput = datePicker.find('.minutes-input');
            const amPmSelect = datePicker.find('.am-pm-select');
            const okBtn = datePicker.find('.ok-btn');

            hoursInput.on('change', handleChange);
            minutesInput.on('change', handleChange);
            amPmSelect.on('change', handleChange);
            okBtn.on('click', handleClickOk);

            datePicker.dataAndAttr('append-time-on-open', true);
        }

        if (datePicker.hasClass('always-open')) {
            // Give some time for the wcdatepicker element to initialize before calling the open method.
            setTimeout(() => {
                openDatePicker({target: dateInput[0]});
            }, 100);
        }

        datePicker.addClass('date-picker-initialized');
        datePicker.trigger(howspace.components.datePicker.DATE_PICKER_INITIALIZED);
    };

    const destroyWcDatepicker = (datePicker) => {
        const wcDatePicker = datePicker.find('wc-datepicker');
        const dateInput = datePicker.find('.howspace-form-date-input');
        const clearDateBtn = datePicker.find('.clear-date-btn');

        if (!datePicker.hasClass('always-open')) {
            // Blur event doesn't bubble so listen to the event in the capture phase.
            datePicker.off('mousedown', handleMouseDown);
            datePicker[0].removeEventListener('blur', handleBlur, true);
            datePicker.off('pointerdown', handlePointerDown);
            dateInput.off('focus', handleInputFocus);
            dateInput.off('click', handleInputClick);
            datePicker.off('click', forgetPointerType);
            dateInput.off('keydown', handleInputKeyDown);
            datePicker.siblings('.howspace-form-label-text').off('click', handleLabelTextClick);
        }

        datePicker.off('keydown', handleKeyDown);
        wcDatePicker.off('selectDate', selectDate);
        clearDateBtn.off('click', clearDate);

        if (datePicker.data('pick-time')) {
            const hoursInput = datePicker.find('.hours-input');
            const minutesInput = datePicker.find('.minutes-input');
            const amPmSelect = datePicker.find('.am-pm-select');
            const okBtn = datePicker.find('.ok-btn');

            hoursInput.off('change', handleChange);
            minutesInput.off('change', handleChange);
            amPmSelect.off('change', handleChange);
            okBtn.off('click', handleClickOk);
        }
    };

    return {
        // Gives focus back to a date input, for example after a re-render, without opening its calendar.
        focusInputWithoutOpening: focusInputWithoutOpening,
        DATE_CHANGED: 'date-changed',
        DATE_PICKER_INITIALIZED: 'date-initialized',
        DATE_PICKER_OPENED: 'date-picker-opened',
        DATE_COMPARED_PASSED: 'date-compared-success',
        DATE_COMPARED_FAILED: 'date-compared-failed',
        DATE_PICKER_CLEARED: 'date-cleared',
        init: (elem) => {
            elem.findIncludingSelf('.howspace-date-picker:not(.howspace-date-picker-initialized)').each(async function () {
                const datePicker = $(this);

                datePicker.addClass('howspace-date-picker-initialized');

                await lib('wc-datepicker', 'wc-datepicker.js');
                initWcDatepicker(datePicker);
            });
        },
        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-form-date-label.howspace-form-date-label-initialized').each(async function () {
                const datePicker = $(this);

                datePicker.removeClass('howspace-date-picker-initialized');

                await lib('wc-datepicker', 'wc-datepicker.js');
                destroyWcDatepicker(datePicker);
            });
        },
        setDate: (datePicker, date) =>  {
            datePicker.dataAndAttr('date', date);
            updateDate(datePicker);
        }
    };
})();
